// Studio panel for the chat2 facilitator pipeline. Shows what the engine
// just did (last-turn stats, frozen-boundary changes, parse errors,
// warnings, attachments) on top of the raw model output. Used during
// development and as a "what's the assistant doing right now?" surface
// during live discovery sessions.

import React, { useMemo, useState } from 'react';
import type { LastTurn, NewModel } from '../utils/flowModel';
import type { AttachmentRecord, ConversationMessage } from '../hooks/useSession';
import { parseFlow } from '../utils/flowParser';
import '../styles/DebugPanel.css';

interface DebugPanelProps {
  newModel: NewModel;
  conversationHistory: ConversationMessage[];
  isOpen: boolean;
  onToggle: () => void;
}

const DebugPanel: React.FC<DebugPanelProps> = ({
  newModel,
  conversationHistory,
  isOpen,
  onToggle,
}) => {
  const parsed = useMemo(() => parseFlow(newModel.flowText || ''), [newModel.flowText]);
  const [showFlow, setShowFlow] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  // Walk history newest-first to find the most recent attachments-bearing turn.
  const attachmentsByTurn = useMemo(() => {
    const out: { timestamp: string; attachments: AttachmentRecord[] }[] = [];
    for (const msg of conversationHistory) {
      if (msg.attachments?.length) {
        out.push({ timestamp: msg.timestamp, attachments: msg.attachments });
      }
    }
    return out.reverse();
  }, [conversationHistory]);

  if (!isOpen) {
    return (
      <button className="dbg-toggle dbg-toggle--closed" onClick={onToggle} title="Show studio panel">
        {'{}'}
      </button>
    );
  }

  const lt = newModel.lastTurn;
  const errorCount = parsed.errors.filter((e) => e.severity === 'error').length;
  const warningCount = parsed.errors.filter((e) => e.severity === 'warning').length;

  return (
    <aside className="dbg-panel">
      <header className="dbg-header">
        <span className="dbg-title">STUDIO</span>
        <button className="dbg-close" onClick={onToggle} title="Hide studio panel">
          ×
        </button>
      </header>

      {/* Last turn — what the engine did on the most recent /chat2 call.
          If the conversation has agent turns but `lastTurn` is missing, the
          session predates the telemetry capture (added 2026-04-28); send
          another message to populate. */}
      <section className="dbg-section">
        <div className="dbg-label">Last turn</div>
        {lt ? (
          <LastTurnView lt={lt} />
        ) : conversationHistory.some((m) => m.type === 'agent') ? (
          <div className="dbg-empty">
            Telemetry not captured on this session yet — populates on the next turn.
          </div>
        ) : (
          <div className="dbg-empty">(no turns yet)</div>
        )}
      </section>

      {/* Frozen boundary — current value + the recent transition if there was
          one. The server's algorithm holds at 0 until the flow has ≥20
          statements and the frozen prefix would be ≥3000 chars, so a small
          flow legitimately stays at 0 even after many turns. */}
      <section className="dbg-section">
        <div className="dbg-label">Frozen boundary</div>
        <FrozenBoundaryView
          current={newModel.frozenBoundary || 0}
          previous={lt?.prevFrozenBoundary}
          invalidated={lt?.boundaryInvalidated}
          statementCount={parsed.statements.length}
        />
      </section>

      {/* Statements + parse health. */}
      <section className="dbg-section">
        <div className="dbg-label">Statements</div>
        <div className="dbg-row">
          <span className="dbg-stat">{parsed.statements.length}</span>
          {errorCount > 0 && <span className="dbg-pill dbg-pill--error">{errorCount} error{errorCount > 1 ? 's' : ''}</span>}
          {warningCount > 0 && <span className="dbg-pill dbg-pill--warn">{warningCount} warning{warningCount > 1 ? 's' : ''}</span>}
        </div>
        {parsed.errors.length > 0 && (
          <ul className="dbg-list">
            {parsed.errors.slice(0, 8).map((e, i) => (
              <li key={i}>
                <strong>{e.code}</strong>
                {e.statementId != null ? ` [${e.statementId}]` : ''}: {e.message}
              </li>
            ))}
            {parsed.errors.length > 8 && (
              <li className="dbg-empty">…+{parsed.errors.length - 8} more</li>
            )}
          </ul>
        )}
      </section>

      {/* Engine warnings (server-side delta-application + reference-check). */}
      {lt?.warnings && lt.warnings.length > 0 && (
        <section className="dbg-section">
          <div className="dbg-label">Engine warnings ({lt.warnings.length + (lt.warningOverflow || 0)})</div>
          <ul className="dbg-list">
            {lt.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
            {lt.warningOverflow > 0 && (
              <li className="dbg-empty">…+{lt.warningOverflow} more not shown</li>
            )}
          </ul>
        </section>
      )}

      {/* Attachments across the session, newest-first. */}
      {attachmentsByTurn.length > 0 && (
        <section className="dbg-section">
          <div className="dbg-label">Attachments</div>
          {attachmentsByTurn.slice(0, 5).map((turn, ti) => (
            <div key={ti} className="dbg-attach-turn">
              <div className="dbg-attach-when">{shortTime(turn.timestamp)}</div>
              {turn.attachments.map((a, ai) => (
                <div key={ai} className="dbg-attach-row">
                  <span className="dbg-attach-kind">{a.kind}</span>
                  <span className="dbg-attach-name" title={a.filename}>{a.filename}</span>
                  <span className="dbg-attach-size">{formatBytes(a.size)}</span>
                </div>
              ))}
            </div>
          ))}
        </section>
      )}

      {/* Next Question — quick reference. */}
      <section className="dbg-section">
        <div className="dbg-label">Next Question</div>
        <div className="dbg-content">
          {newModel.nextQuestion || <span className="dbg-empty">(none)</span>}
        </div>
      </section>

      {/* Open Questions — full text. */}
      <section className="dbg-section">
        <div className="dbg-label">Open Questions</div>
        {!newModel.openQuestions.trim() ? (
          <div className="dbg-empty">(none)</div>
        ) : (
          <pre className="dbg-code">{newModel.openQuestions}</pre>
        )}
      </section>

      {/* Flow text — collapsible because it gets long. */}
      <section className="dbg-section">
        <button
          type="button"
          className="dbg-collapse"
          onClick={() => setShowFlow((v) => !v)}
          aria-expanded={showFlow}
        >
          <span className="dbg-collapse-arrow">{showFlow ? '▾' : '▸'}</span>
          <span className="dbg-label dbg-label--inline">
            Flow text ({parsed.statements.length} statements)
          </span>
        </button>
        {showFlow && (
          newModel.flowText.trim() ? (
            <pre className="dbg-code">{newModel.flowText}</pre>
          ) : (
            <div className="dbg-empty">(empty)</div>
          )
        )}
      </section>

      {/* Raw model response — collapsible because it's the largest blob. */}
      <section className="dbg-section">
        <button
          type="button"
          className="dbg-collapse"
          onClick={() => setShowRaw((v) => !v)}
          aria-expanded={showRaw}
        >
          <span className="dbg-collapse-arrow">{showRaw ? '▾' : '▸'}</span>
          <span className="dbg-label dbg-label--inline">
            Raw response ({(newModel.raw || '').length} chars)
          </span>
        </button>
        {showRaw && (
          <pre className="dbg-code dbg-code--small">{newModel.raw || '(none)'}</pre>
        )}
      </section>
    </aside>
  );
};

export default DebugPanel;

// ─── Sub-views ────────────────────────────────────────────────────

function LastTurnView({ lt }: { lt: LastTurn }) {
  const modeClass = lt.mode === 'full-rewrite' ? 'dbg-pill--warn' : 'dbg-pill--ok';
  const stmtDelta = lt.statementCount - lt.prevStatementCount;
  return (
    <>
      <div className="dbg-row">
        <span className={`dbg-pill ${modeClass}`}>{lt.mode}</span>
        {lt.mode === 'delta' && (
          <>
            {lt.additions != null && lt.additions > 0 && (
              <span className="dbg-pill dbg-pill--ok">+{lt.additions} added</span>
            )}
            {lt.revisions != null && lt.revisions > 0 && (
              <span className="dbg-pill dbg-pill--neutral">{lt.revisions} revised</span>
            )}
          </>
        )}
        {lt.attachmentCount > 0 && (
          <span className="dbg-pill dbg-pill--neutral">{lt.attachmentCount} file{lt.attachmentCount > 1 ? 's' : ''}</span>
        )}
      </div>
      <dl className="dbg-kv">
        <dt>statements</dt>
        <dd>
          {lt.statementCount}
          {stmtDelta !== 0 && (
            <span className={stmtDelta > 0 ? 'dbg-delta-pos' : 'dbg-delta-neg'}>
              {' '}({stmtDelta > 0 ? '+' : ''}{stmtDelta})
            </span>
          )}
        </dd>
        <dt>duration</dt>
        <dd>
          {ms(lt.durationMs)}
          {lt.nqFirstByteMs != null && <span className="dbg-dim"> · NQ@{ms(lt.nqFirstByteMs)}</span>}
        </dd>
        <dt>tokens</dt>
        <dd>
          <span title="input chars">in {chars(lt.inputChars)}</span>
          <span className="dbg-dim"> · </span>
          <span title="output chars">out {chars(lt.outputChars)}</span>
        </dd>
        <dt>model</dt>
        <dd className="dbg-mono">{lt.model}</dd>
        <dt>at</dt>
        <dd className="dbg-dim">{shortTime(lt.timestamp)}</dd>
      </dl>
    </>
  );
}

// Threshold mirrored from functions/_chat2Helpers.js — the algorithm needs
// at least TAIL_STATEMENTS * 2 statements before a freeze can happen.
const FREEZE_MIN_STATEMENTS = 20;

function FrozenBoundaryView({
  current,
  previous,
  invalidated,
  statementCount,
}: {
  current: number;
  previous?: number;
  invalidated?: boolean;
  statementCount: number;
}) {
  if (current === 0 && (previous == null || previous === 0)) {
    if (statementCount === 0) {
      return <div className="dbg-empty">(none — no statements yet)</div>;
    }
    if (statementCount < FREEZE_MIN_STATEMENTS) {
      return (
        <div>
          <div className="dbg-row">
            <span className="dbg-stat">0</span>
            <span className="dbg-pill dbg-pill--neutral">warming up</span>
          </div>
          <div className="dbg-empty" style={{ marginTop: 4, fontSize: 10.5 }}>
            Boundary holds at 0 until ≥{FREEZE_MIN_STATEMENTS} statements.
            Currently {statementCount}/{FREEZE_MIN_STATEMENTS}.
          </div>
        </div>
      );
    }
    return (
      <div>
        <div className="dbg-row">
          <span className="dbg-stat">0</span>
          <span className="dbg-pill dbg-pill--neutral">below char threshold</span>
        </div>
        <div className="dbg-empty" style={{ marginTop: 4, fontSize: 10.5 }}>
          Frozen prefix would be under 3000 chars (~750 tokens) — engine
          keeps everything live until the cache split would actually pay off.
        </div>
      </div>
    );
  }
  const advanced = previous != null && current > previous;
  const receded = previous != null && current < previous;
  return (
    <div className="dbg-row">
      <span className="dbg-stat">[{current}]</span>
      {advanced && (
        <span className="dbg-pill dbg-pill--ok">advanced from [{previous}]</span>
      )}
      {receded && (
        <span className="dbg-pill dbg-pill--warn">receded from [{previous}]</span>
      )}
      {invalidated && (
        <span className="dbg-pill dbg-pill--warn">invalidated this turn</span>
      )}
      {previous != null && current === previous && !invalidated && (
        <span className="dbg-pill dbg-pill--neutral">unchanged</span>
      )}
    </div>
  );
}

// ─── Formatters ───────────────────────────────────────────────────

function ms(n: number): string {
  if (n < 1000) return `${n}ms`;
  return `${(n / 1000).toFixed(1)}s`;
}

function chars(n: number): string {
  if (n < 1000) return `${n}`;
  if (n < 1000000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1000000).toFixed(1)}M`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}

function shortTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return iso;
  }
}
